import React, { useState, useEffect } from 'react';
import Preloader from '../Preloader';
import { getDatabase, ref, get } from 'firebase/database';

const StudentSessionList = ({ sessions, classId, user, grades }) => {
  const [sessionSearchTerm, setSessionSearchTerm] = useState('');
  const [expandedSessions, setExpandedSessions] = useState({});
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({});

  const handleSessionSearch = (e) => {
    setSessionSearchTerm(e.target.value);
  };

  const toggleSessionExpand = (sessionId) => {
    setExpandedSessions({
      ...expandedSessions,
      [sessionId]: !expandedSessions[sessionId],
    });
  };

  // Fetch student's answers from Realtime Database
  useEffect(() => {
    const fetchAnswers = async () => {
      setLoading(true);
      const db = getDatabase();
      const studentUid = user.uid;
      const answersRef = ref(db, `answers/${classId}`);

      try {
        const snapshot = await get(answersRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          const studentAnswers = {};

          Object.entries(data).forEach(([sessionId, sessionData]) => {
            Object.entries(sessionData).forEach(([questionNumber, questionData]) => {
              if (questionData[studentUid]) {
                if (!studentAnswers[sessionId]) {
                  studentAnswers[sessionId] = {};
                }
                studentAnswers[sessionId][questionNumber] = questionData[studentUid];
              }
            });
          });

          setAnswers(studentAnswers);
        } else {
          setAnswers({});
        }
      } catch (error) {
        console.error('Error fetching answers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnswers();
  }, [classId, user.uid]);

  // Filter sessions based on search term
  const filteredSessions = sessions.filter((session) => {
    const sessionDate = new Date(session.start_date_time);
    return sessionDate.toLocaleDateString().includes(sessionSearchTerm);
  });

  if (loading) {
    return <Preloader />;
  }

  // Function to get display name for question type
  const getQuestionTypeDisplayName = (type) => {
    switch (type) {
      case 'multiple_choice':
        return 'Multiple Choice';
      case 'multiple_multiple_choice':
        return 'Multiple Answers';
      case 'yes_no':
        return 'Yes/No';
      case 'short_input':
        return 'Input';
      default:
        return type;
    }
  };

  return (
    <div className="w-full min-w-0 px-3 sm:px-4 py-4">
      <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-color)' }}>
        Sessions
      </h3>
      <input
        type="text"
        placeholder="Search Sessions..."
        value={sessionSearchTerm}
        onChange={handleSessionSearch}
        className="w-full p-2 mb-4 border border-[var(--muted-color)] rounded bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent transition-all"
        style={{
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text-color)',
          borderColor: 'var(--muted-color)'
        }}
      />

      <div className="space-y-4">
        {filteredSessions.map((session) => {
          const questionNumbers = Object.keys(session.questions || {}).map(Number);
          const maxQuestionNumber = questionNumbers.length > 0 ? Math.max(...questionNumbers) : 0;
          return (
            <div key={session.id} className="border rounded shadow-sm" style={{ borderColor: 'var(--muted-color)' }}>
              <div
                className="flex justify-between items-center p-3 rounded cursor-pointer"
                style={{ backgroundColor: 'var(--card-hover-bg)' }}
                onClick={() => toggleSessionExpand(session.id)}
              >
                <h4 className="text-lg font-medium" style={{ color: 'var(--text-color)' }}>
                  Session on {new Date(session.start_date_time).toLocaleString()}
                </h4>
                <span className="text-xl" style={{ color: 'var(--text-color)' }}>
                  {expandedSessions[session.id] ? '▴' : '▾'}
                </span>
              </div>

              {expandedSessions[session.id] && (
                <div className="p-4" style={{ backgroundColor: 'var(--card-bg)' }}>
                  <p className="mb-4" style={{ color: 'var(--text-color)' }}>
                    <strong>Total Questions:</strong> {maxQuestionNumber}
                  </p>
                  {session.questions &&
                    Object.entries(session.questions).map(([questionNumber, question]) => (
                      <div
                        key={questionNumber}
                        className="mb-4 p-4 border rounded"
                        style={{ borderColor: 'var(--muted-color)' }}
                      >
                        <h5 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-color)' }}>
                          Question {questionNumber}
                        </h5>

                        {question.question_photo ? (
                          question.question_photo === 'InClass' ? (
                            <div
                              className="p-3 mb-3 rounded"
                              style={{ backgroundColor: 'var(--alt-bg)', color: 'var(--text-color)' }}
                            >
                              This question was presented in the class.
                            </div>
                          ) : (
                            <div className="mb-3">
                              <img
                                src={question.question_photo}
                                alt={`Question ${questionNumber}`}
                                className="mb-3 w-full rounded"
                              />
                            </div>
                          )
                        ) : null}

                        <p className="mb-2" style={{ color: 'var(--text-color)' }}>
                          <strong>Type:</strong> {getQuestionTypeDisplayName(question.question_type)}
                        </p>

                        {question.possible_answers && (
                          <div className="mb-2">
                            <strong style={{ color: 'var(--text-color)' }}>Possible Answers:</strong>
                            <ul className="list-disc ml-5 mt-2">
                              {question.possible_answers.map((answer, index) => (
                                <li key={index} style={{ color: 'var(--text-color)' }}>
                                  {answer}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <p className="mb-2" style={{ color: 'var(--text-color)' }}>
                          <strong>Correct Answer:</strong>{' '}
                          {Array.isArray(question.correct_answer)
                            ? question.correct_answer.join(', ')
                            : question.correct_answer || 'Not set'}
                        </p>

                        <p className="mb-2" style={{ color: 'var(--text-color)' }}>
                          <strong>Your Answer:</strong>{' '}
                          {answers[session.id]?.[questionNumber]?.answer || 'Not Answered'}
                        </p>

                        <p className="mb-3" style={{ color: 'var(--text-color)' }}>
                          <strong>Points Earned:</strong>{' '}
                          {grades[session.id]?.points?.[questionNumber] ?? '0'}
                        </p>
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudentSessionList;
